import { ArrowUpRight, ArrowDownRight } from "lucide-react";
const CFDI_T = { I: "Ingreso", E: "Egreso", P: "Pago", N: "Nómina", T: "Traslado" };
const cfdiTipo = (k) => { const [d, t] = k.split("_"); return `${CFDI_T[t] || t} ${d === "emitida" ? "emitido" : d === "recibida" ? "recibido" : d}`; };
import { fmtMXN, fmtNum, fmtPct } from "../../lib/api";

const Kpi = ({ label, value, sub, testid, icon }) => (
  <div className="border-t-2 border-orange-500 pt-3" data-testid={testid}>
    <p className="eyebrow flex items-center gap-1">{icon}{label}</p>
    <p className="font-serif-display mt-1 text-2xl font-bold">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-zinc-500">{sub}</p>}
  </div>
);

const TopTable = ({ rows, testid, title }) => (
  <div>
    <h3 className="mb-2 text-sm font-semibold">{title}</h3>
    <table className="w-full text-sm" data-testid={testid}>
      <thead><tr className="bg-[#111111] text-left text-[10px] uppercase tracking-wider text-white">
        <th className="px-3 py-2">RFC</th><th className="px-3 py-2">Razón social</th><th className="px-3 py-2 text-right">Neto MXN</th><th className="px-3 py-2 text-right">%</th>
      </tr></thead>
      <tbody>{rows.slice(0, 5).map((r) => (
        <tr key={r.rfc} className="row-hover border-b border-zinc-100">
          <td className="px-3 py-2 font-mono text-xs">{r.rfc}</td>
          <td className="max-w-[220px] truncate px-3 py-2">{r.nombre}</td>
          <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.monto)}</td>
          <td className="px-3 py-2 text-right tabular-nums">{fmtPct(r.pct)}</td>
        </tr>))}
      </tbody>
    </table>
  </div>
);

const MonthlyBars = ({ mensual }) => {
  const max = Math.max(...mensual.map((m) => Math.max(m.emitidas_neto, m.recibidas_neto)), 1);
  return (
    <div className="mt-2" data-testid="cfdi-monthly-chart">
      <div className="flex h-40 items-end gap-1.5">
        {mensual.map((m) => (
          <div key={m.mes} className="group flex flex-1 items-end gap-0.5" title={`${m.mes}: emitida ${fmtNum(m.emitidas_neto)} · recibida ${fmtNum(m.recibidas_neto)}`}>
            <div className="w-1/2 bg-[#111111] transition-opacity group-hover:opacity-80" style={{ height: `${(m.emitidas_neto / max) * 160}px` }} />
            <div className="w-1/2 bg-orange-500 transition-opacity group-hover:opacity-80" style={{ height: `${(Math.max(m.recibidas_neto, 0) / max) * 160}px` }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1.5">{mensual.map((m) => <span key={m.mes} className="flex-1 text-center font-mono text-[9px] text-zinc-400">{m.mes.slice(2)}</span>)}</div>
      <div className="mt-2 flex gap-4 text-xs text-zinc-500"><span className="flex items-center gap-1"><i className="inline-block h-2 w-3 bg-[#111111]" /> Emitida neta</span><span className="flex items-center gap-1"><i className="inline-block h-2 w-3 bg-orange-500" /> Recibida neta</span></div>
    </div>
  );
};

export const CfdiSummary = ({ result }) => {
  if (!result) return null;
  const em = result.anual.reduce((s, a) => s + a.emitidas_neto, 0);
  const re = result.anual.reduce((s, a) => s + a.recibidas_neto, 0);
  const ex = result.excluidos;
  return (
    <section className="panel p-6 lg:p-8" data-testid="cfdi-summary">
      <p className="eyebrow">Facturación SAT</p>
      <h2 className="panel-title mt-1 text-xl sm:text-2xl">Facturación SAT · RFC <span className="font-mono text-lg" data-testid="cfdi-rfc">{result.rfc_expediente}</span></h2>
      <div className="stagger mt-6 grid grid-cols-2 gap-6 lg:grid-cols-4">
        <Kpi testid="cfdi-emitida-kpi" icon={<ArrowUpRight size={12} />} label="Emitida neta" value={fmtMXN(em)} sub={result.anual.map((a) => `${a.anio}: ${fmtNum(a.emitidas_neto)}`).join(" · ")} />
        <Kpi testid="cfdi-recibida-kpi" icon={<ArrowDownRight size={12} />} label="Recibida neta" value={fmtMXN(re)} sub={result.anual.map((a) => `${a.anio}: ${fmtNum(a.recibidas_neto)}`).join(" · ")} />
        <Kpi testid="cfdi-validos-kpi" label="CFDI válidos" value={fmtNum(result.cfdi_validos)} sub={`${result.archivos_xml} XML leídos`} />
        <Kpi testid="cfdi-margen-kpi" label="Margen CFDI" value={fmtPct(em ? (em - re) / em : null)} sub="(emitida − recibida) ÷ emitida" />
      </div>
      <div className="mt-8 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-7"><h3 className="text-sm font-semibold">Facturación mensual (MXN sin IVA)</h3><MonthlyBars mensual={result.mensual} /></div>
        <div className="lg:col-span-5 border border-zinc-200 p-5" data-testid="cfdi-exclusions-panel">
          <h3 className="text-sm font-semibold">Exclusiones</h3>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between"><dt>Cancelados (metadata SAT)</dt><dd className="font-semibold tabular-nums" data-testid="cfdi-cancelados-count">{ex.cancelados.cantidad} · {fmtNum(ex.cancelados.monto_neto_mxn)}</dd></div>
            <div className="flex justify-between"><dt>Duplicados (UUID)</dt><dd className="font-semibold tabular-nums" data-testid="cfdi-duplicados-count">{ex.duplicados.cantidad}</dd></div>
            <div className="flex justify-between"><dt>Ajenos al RFC</dt><dd className="font-semibold tabular-nums">{ex.ajenos_al_rfc.cantidad}</dd></div>
            <div className="flex justify-between"><dt>XML con error</dt><dd className="font-semibold tabular-nums">{ex.errores_count}</dd></div>
          </dl>
          <h3 className="mt-5 text-sm font-semibold">Por tipo</h3>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="cfdi-por-tipo">
            {Object.entries(result.por_tipo).map(([k, v]) => <span key={k} className="rounded-full border border-zinc-200 px-2.5 py-0.5 text-[11px]">{cfdiTipo(k)}: {v}</span>)}
          </div>
        </div>
      </div>
      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <TopTable title="Top clientes" rows={result.top_clientes} testid="cfdi-top-clientes-table" />
        <TopTable title="Top proveedores" rows={result.top_proveedores} testid="cfdi-top-proveedores-table" />
      </div>
    </section>
  );
};
